import { GetThunkAPI } from '@reduxjs/toolkit/dist/createAsyncThunk';
import RequestService, {
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectQuestionnaire from '../types/project/ProjectQuestionnaire';
import { NotificationActions } from '../../common/actions/NotificationActions';
import Section from '../types/project/360/Section';
import Subsection from '../types/project/360/Subsection';
import Question from '../types/project/360/Question';
import ProjectTranslation from '../types/project/ProjectTranslation';

const Admin360QuestionnaireService = {
    getQuestionnaires,
    getQuestionnaire,
    createQuestionnaire,
    updateQuestionnaire,
    getSections,
    getSection,
    createSection,
    updateSection,
    getSubsections,
    getSubsection,
    createSubsection,
    updateSubsection,
    getQuestions,
    getQuestion,
    createQuestion,
    updateQuestion,
    deleteQuestion,
    getTranslations,
    saveTranslations,
    addLanguage,
};

const BASE_URL = '/admin/products/360/questionnaires';

async function getQuestionnaires(
    payload: { page?: number },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<ProjectQuestionnaire[]>> {
    const { page = 1 } = payload;
    try {
        return await RequestService.get<any>(`${BASE_URL}`, { page });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist dotazniky'),
        ); // TODO: translate

        throw e;
    }
}

async function getQuestionnaire(
    payload: { questionnaireId: number },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    try {
        return await RequestService.get<ProjectQuestionnaire>(
            `${BASE_URL}/${payload.questionnaireId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist dotaznik'),
        ); // TODO: translate

        throw e;
    }
}

async function createQuestionnaire(
    payload: { name: string; primaryLanguage: string },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { name, primaryLanguage } = payload;
    try {
        return await RequestService.post<any>(`${BASE_URL}`, {
            name,
            primaryLanguage,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit dotaznik'),
        ); // TODO: translate

        throw e;
    }
}

async function updateQuestionnaire(
    payload: {
        questionnaireId: number;
        name?: string;
        primaryLanguage?: string;
        timeToFill?: string;
        description?: string;
        config?: {
            freeCommentText: string;
            openingText: string;
        };
        scale?: { id: number };
    },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<ProjectQuestionnaire[]>> {
    const {
        questionnaireId,
        name,
        primaryLanguage,
        timeToFill,
        config,
        description,
        scale,
    } = payload;
    try {
        return await RequestService.post<any>(
            `${BASE_URL}/${questionnaireId}`,
            {
                name,
                primaryLanguage,
                timeToFill,
                config,
                description,
                scale,
            },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit dotaznik'),
        ); // TODO: translate

        throw e;
    }
}

const getSectionsUrl = (questionnaireId: number): string =>
    `${BASE_URL}/${questionnaireId}/sections`;

async function getSections(
    payload: { questionnaireId: number },
    thunkApi: GetThunkAPI<any>,
) {
    try {
        return await RequestService.get<Section[]>(
            getSectionsUrl(payload.questionnaireId),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist sekce'),
        ); // TODO: translate

        throw e;
    }
}

async function getSection(
    payload: { questionnaireId: number; sectionId: number },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId } = payload;
    try {
        return await RequestService.get<Section>(
            `${getSectionsUrl(questionnaireId)}/${sectionId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist sekci'),
        ); // TODO: translate

        throw e;
    }
}

async function createSection(
    payload: { questionnaireId: number; name: string },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, name } = payload;
    try {
        return await RequestService.post<{ name: string }, Section>(
            getSectionsUrl(questionnaireId),
            {
                name,
            },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit sekci'),
        ); // TODO: translate

        throw e;
    }
}

async function updateSection(
    payload: {
        questionnaireId: number;
        sectionId: number;
        name?: string;
        order?: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, name, order } = payload;
    try {
        return await RequestService.put<
            { name?: string; order?: number },
            Section
        >(`${getSectionsUrl(questionnaireId)}/${sectionId}`, { name, order });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit sekci'),
        ); // TODO: translate

        throw e;
    }
}

const getSubsectionsUrl = (
    questionnaireId: number,
    sectionId: number,
): string => `${getSectionsUrl(questionnaireId)}/${sectionId}/subsections`;

async function getSubsections(
    payload: { questionnaireId: number; sectionId: number },
    thunkApi: GetThunkAPI<any>,
) {
    try {
        return await RequestService.get<Subsection[]>(
            getSubsectionsUrl(payload.questionnaireId, payload.sectionId),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist sekce'),
        ); // TODO: translate

        throw e;
    }
}

async function getSubsection(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId } = payload;
    try {
        return await RequestService.get<Subsection>(
            `${getSubsectionsUrl(questionnaireId, sectionId)}/${subsectionId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist podseckci'),
        ); // TODO: translate

        throw e;
    }
}

async function createSubsection(
    payload: { questionnaireId: number; sectionId: number; name: string },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, name } = payload;
    try {
        return await RequestService.post<{ name: string }, Subsection>(
            getSubsectionsUrl(questionnaireId, sectionId),
            {
                name,
            },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit podseckci'),
        ); // TODO: translate

        throw e;
    }
}

async function updateSubsection(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
        name?: string;
        order?: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId, name, order } = payload;
    try {
        return await RequestService.put<
            { name?: string; order?: number },
            Subsection
        >(`${getSubsectionsUrl(questionnaireId, sectionId)}/${subsectionId}`, {
            name,
            order,
        });
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit podseckci'),
        ); // TODO: translate

        throw e;
    }
}

const getQuestionsUrl = (
    questionnaireId: number,
    sectionId: number,
    subsectionId: number,
): string =>
    `${getSubsectionsUrl(questionnaireId, sectionId)}/${subsectionId}/questions`;

async function getQuestions(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId } = payload;
    try {
        return await RequestService.get<Question[]>(
            getQuestionsUrl(questionnaireId, sectionId, subsectionId),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist sekce'),
        ); // TODO: translate

        throw e;
    }
}

async function getQuestion(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
        questionId: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId, questionId } = payload;
    try {
        return await RequestService.get<Question>(
            `${getQuestionsUrl(questionnaireId, sectionId, subsectionId)}/${questionId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist podseckci'),
        ); // TODO: translate

        throw e;
    }
}

type QuestionType = 0 | 1;

interface QuestionDataPayload {
    text: string;
    type: QuestionType;
}

interface QuestionPayload extends QuestionDataPayload {
    questionnaireId: number;
    sectionId: number;
    subsectionId: number;
}

async function createQuestion(
    payload: QuestionPayload,
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId, text, type } = payload;
    try {
        return await RequestService.post<QuestionDataPayload, Question>(
            getQuestionsUrl(questionnaireId, sectionId, subsectionId),
            {
                text,
                type,
            },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se vytvorit otazku'),
        ); // TODO: translate

        throw e;
    }
}

async function updateQuestion(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
        questionId: number;
        text?: string;
        order?: number;
        type?: QuestionType;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const {
        questionnaireId,
        sectionId,
        subsectionId,
        questionId,
        text,
        order,
        type,
    } = payload;
    try {
        return await RequestService.patch<
            { text?: string; order?: number; type?: QuestionType },
            Question
        >(
            `${getQuestionsUrl(questionnaireId, sectionId, subsectionId)}/${questionId}`,
            { text, order, type },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se upravit otazky'),
        ); // TODO: translate

        throw e;
    }
}

async function deleteQuestion(
    payload: {
        questionnaireId: number;
        sectionId: number;
        subsectionId: number;
        questionId: number;
    },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, sectionId, subsectionId, questionId } = payload;
    try {
        return await RequestService.delete(
            `${getQuestionsUrl(questionnaireId, sectionId, subsectionId)}/${questionId}`,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se smazat otazku'),
        ); // TODO: translate

        throw e;
    }
}

const getTranslationsUrl = (questionnaireId: number, locale: string): string =>
    `${BASE_URL}/${questionnaireId}/translations?locale=${locale}`;

async function getTranslations(
    payload: { questionnaireId: number; locale: string },
    thunkApi: GetThunkAPI<any>,
) {
    const { questionnaireId, locale } = payload;
    try {
        return await RequestService.get<ProjectTranslation[]>(
            getTranslationsUrl(questionnaireId, locale),
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se nacist preklady'),
        ); // TODO: translate

        throw e;
    }
}

async function addLanguage(
    payload: {
        questionnaireId: number;
        locale: string;
        locales: string[];
    },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<ProjectQuestionnaire>> {
    const { questionnaireId, locale, locales } = payload;
    try {
        return await RequestService.patch<any>(
            `${BASE_URL}/${questionnaireId}`,
            { locales: [...locales, locale] },
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se ulozit jazyk'),
        ); // TODO: translate

        throw e;
    }
}

async function saveTranslations(
    payload: {
        questionnaireId: number;
        locale: string;
        translations: ProjectTranslation[];
    },
    thunkApi: GetThunkAPI<any>,
): Promise<RequestServiceResponse<any>> {
    const { questionnaireId, locale, translations } = payload;
    try {
        return await RequestService.patch<any>(
            getTranslationsUrl(questionnaireId, locale),
            translations,
        );
    } catch (e) {
        thunkApi.dispatch(
            NotificationActions.error('nepodarilo se ulozit preklady'),
        ); // TODO: translate

        throw e;
    }
}

export default Admin360QuestionnaireService;
